// Keep these faces before Next's Latin faces: overlapping Unicode ranges use the last matching face.
import "./fonts/inter.module.css";

import { createTheme, defaultVariantColorsResolver, parseThemeColor, rem, rgba, type VariantColorsResolver } from "@mantine/core";
import localFont from "next/font/local";
// Mantine 7's static stylesheets, core first (SC-2703). They live in this private vendor file, not in
// ShelfCycleProvider, because declaration emit keeps a bare `import "…"` and the lint:ui-types gate
// would read the provider's six vendor specifiers as leaks; this file's declaration is not scanned.
// The provider imports this module first, so the stylesheets still precede the app's own CSS.
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/dropzone/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/tiptap/styles.css";
import "mantine-datatable/styles.css";

import { mantineColorRamps, tokens } from "../tokens";

import { components } from "./components";

// Preserve Google's Latin-only preload and four weights without a build-time font request.
export const InterFont = localFont({
  src: [
    { path: "./fonts/Inter-latin.woff2", weight: "400" },
    { path: "./fonts/Inter-latin.woff2", weight: "500" },
    { path: "./fonts/Inter-latin.woff2", weight: "600" },
    { path: "./fonts/Inter-latin.woff2", weight: "700" },
  ],
  style: "normal",
  display: "swap",
  preload: true,
  // The adjacent stylesheet retains the Google loader's original Arial fallback metrics.
  adjustFontFallback: false,
  fallback: ["InterFont Fallback"],
  declarations: [
    { prop: "font-family", value: "InterFont" },
    {
      prop: "unicode-range",
      // Next requires a literal; the continuation keeps the original ranges within the line limit.
      value:
        "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC,\
        U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD",
    },
  ],
});
const { fontFamily: defaultFontFamily } = InterFont.style;

/**
 * Mantine 6 drew the light, subtle, outline and default variants from solid palette steps — light is
 * step 0 behind step-`primaryShade` text with a step-1 hover at 65% — where Mantine 7 draws them from
 * step 6 at 5–12% alpha (SC-2703). For a theme color with no explicit shade this returns Mantine 6's
 * values (light color scheme, the only one the app renders); every other input is Mantine 7's own.
 */
export const variantColorResolver: VariantColorsResolver = input => {
  const resolved = defaultVariantColorsResolver(input);
  if (input.variant === "default") {
    return { ...resolved, color: input.theme.black };
  }

  const parsed = parseThemeColor({ color: input.color, theme: input.theme });
  // Mantine sets `isThemeColor` only when the name is a key of `theme.colors`, so a theme color always has its
  // ramp; the index type cannot say so, and the check below keeps a missing one on Mantine 7's own values.
  const ramp = parsed.isThemeColor ? input.theme.colors[parsed.color] : undefined;
  if (ramp === undefined || parsed.shade !== undefined) {
    return resolved;
  }

  const shade = typeof input.theme.primaryShade === "number" ? input.theme.primaryShade : input.theme.primaryShade.light;
  const text = parsed.color === "dark" ? input.theme.colors.dark[9] : ramp[shade];

  switch (input.variant) {
    case "light":
      return { ...resolved, background: ramp[0], hover: rgba(ramp[1], 0.65), color: text };
    case "subtle":
      return { ...resolved, hover: ramp[0], color: text };
    case "outline":
      return { ...resolved, hover: rgba(ramp[0], 0.35), color: ramp[shade], border: `${rem(1)} solid ${ramp[shade]}` };
    default:
      return resolved;
  }
};

export const theme = createTheme({
  // Mantine 7 merges this override onto its default theme; there is no DEFAULT_THEME spread.
  components,
  colors: {
    dark: mantineColorRamps.dark,
    indigo: mantineColorRamps.indigo,
    blue: mantineColorRamps.blue,
    primary: mantineColorRamps.indigo,
    red: mantineColorRamps.danger,
    yellow: mantineColorRamps.yellow,
    green: mantineColorRamps.green,
    neutral: mantineColorRamps.gray,
    gray: mantineColorRamps.gray,
    orange: mantineColorRamps.orange,
    brown: mantineColorRamps.brown,
    violet: mantineColorRamps.violet,
    lime: mantineColorRamps.lime,
    teal: mantineColorRamps.teal,
    cyan: mantineColorRamps.cyan,
    grape: mantineColorRamps.grape,
    pink: mantineColorRamps.pink,
    heading: mantineColorRamps.heading,
    body: mantineColorRamps.body,
  },
  // The Mantine 6 `colorScheme: "light"` is ShelfCycleProvider's `forceColorScheme` in Mantine 7.
  defaultRadius: "xs",
  fontFamily: defaultFontFamily,
  fontSizes: tokens.typography.fontSize,
  headings: {
    fontFamily: defaultFontFamily,
    fontWeight: String(tokens.typography.headings.defaultWeight),
    sizes: {
      h1: { ...tokens.typography.headings.h1, fontWeight: String(tokens.typography.headings.h1.fontWeight) },
      h2: { ...tokens.typography.headings.h2, fontWeight: String(tokens.typography.headings.h2.fontWeight) },
      h3: { ...tokens.typography.headings.h3, fontWeight: String(tokens.typography.headings.h3.fontWeight) },
      h4: { ...tokens.typography.headings.h4, fontWeight: String(tokens.typography.headings.h4.fontWeight) },
      h5: { ...tokens.typography.headings.h5, fontWeight: String(tokens.typography.headings.h5.fontWeight) },
      h6: { ...tokens.typography.headings.h6, fontWeight: String(tokens.typography.headings.h6.fontWeight) },
    },
  },
  primaryColor: "primary",
  primaryShade: 6,
  // Mantine 6's default theme respected the user's reduced-motion setting; Mantine 7's does not (SC-2703).
  respectReducedMotion: true,
  variantColorResolver,
  radius: tokens.radius,
  shadows: tokens.shadow,
  spacing: tokens.spacing,
  black: tokens.color.black,
  white: tokens.color.white,
  other: {
    fontWeights: tokens.typography.fontWeight,
    sizes: tokens.typography.size,
    textLineHeights: tokens.typography.textLineHeight,
  },
});
